import { brandColor } from '../../../_helpers/brand-color';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Subscription, skip } from 'rxjs';
import { ChartData, ChartOptions } from 'chart.js';

import { BaseComponent } from '../../../components/base/base.component';
import { SessionService } from '../../../_services/session/session.service';
import { StatsFilterService } from '../statistics-filter.service';
import {
  StatsDataService,
  DashboardKpis,
  TimeSeriesPoint,
  TopItem,
  CategoryData,
} from '../statistics-data.service';

// Components
import { KpiCardComponent } from '../_components/kpi-card/kpi-card.component';
import { StatChartComponent } from '../_components/stat-chart/stat-chart.component';
import { TopListComponent } from '../_components/top-list/top-list.component';

@Component({
  standalone: true,
  selector: 'app-stats-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    KpiCardComponent,
    StatChartComponent,
    TopListComponent,
  ],
})
export class StatsDashboardComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  private subscriptions: Subscription[] = [];

  // Loading states
  public loadingKpis: boolean = true;
  public loadingChart: boolean = true;
  public loadingTreatments: boolean = true;
  public loadingPatients: boolean = true;
  public loadingPayments: boolean = true;

  // Data
  public kpis: DashboardKpis | null = null;
  public revenueTimeline: TimeSeriesPoint[] = [];
  public topTreatments: TopItem[] = [];
  public topPatients: TopItem[] = [];
  public paymentMethods: CategoryData[] = [];

  // Chart data
  public revenueChartData: ChartData | null = null;
  public revenueChartOptions: ChartOptions = {};
  public paymentChartData: ChartData | null = null;
  public paymentChartOptions: ChartOptions = {};

  // Insights
  public insights: {
    icon: string;
    type: 'success' | 'warning' | 'info';
    message: string;
  }[] = [];

  constructor(
    public session: SessionService,
    private filterService: StatsFilterService,
    private dataService: StatsDataService,
    private router: Router,
    private translate: TranslateService,
  ) {
    super(session);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.loadData();

    // Reload when filters change (skip first emission since we already loaded)
    this.subscriptions.push(
      this.filterService.filters$.pipe(skip(1)).subscribe(() => {
        this.loadData();
      }),
    );

    this.subscriptions.push(
      this.translate.onLangChange.subscribe(() => {
        if (this.revenueTimeline.length) this.buildRevenueChart();
        if (this.paymentMethods.length) this.buildPaymentChart();
        this.generateInsights();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  // =========================================================================
  // DATA LOADING
  // =========================================================================

  loadData(): void {
    const customerId = this.session.getCustomer()?.id;
    const siteId = this.session.getSite()?.id;

    if (!customerId || !siteId) return;

    // Load KPIs
    this.loadingKpis = true;
    this.dataService.getDashboardKpis(customerId, siteId).subscribe({
      next: (kpis) => {
        this.kpis = kpis;
        this.loadingKpis = false;
        this.generateInsights();
      },
      error: () => {
        this.loadingKpis = false;
      },
    });

    // Load Revenue Timeline
    this.loadingChart = true;
    const granularity = this.filterService.currentFilters.granularity;
    this.dataService
      .getRevenueTimeline(customerId, siteId, granularity)
      .subscribe({
        next: (data) => {
          this.revenueTimeline = data;
          this.buildRevenueChart();
          this.loadingChart = false;
        },
        error: () => {
          this.loadingChart = false;
        },
      });

    // Load Top Treatments
    this.loadingTreatments = true;
    this.dataService.getTopTreatments(customerId, siteId, 5).subscribe({
      next: (data) => {
        this.topTreatments = data;
        this.loadingTreatments = false;
      },
      error: () => {
        this.loadingTreatments = false;
      },
    });

    // Load Top Patients
    this.loadingPatients = true;
    this.dataService.getTopPatients(customerId, siteId, 5).subscribe({
      next: (data) => {
        this.topPatients = data.map((item) => ({
          ...item,
          routerLink: this.getPatientRouterLink(item.id),
        }));
        this.loadingPatients = false;
      },
      error: () => {
        this.loadingPatients = false;
      },
    });

    // Load Payment Methods
    this.loadingPayments = true;
    this.dataService
      .getPaymentMethodDistribution(customerId, siteId)
      .subscribe({
        next: (data) => {
          this.paymentMethods = data;
          this.buildPaymentChart();
          this.loadingPayments = false;
        },
        error: () => {
          this.loadingPayments = false;
        },
      });
  }

  // =========================================================================
  // CHART BUILDERS
  // =========================================================================

  private buildRevenueChart(): void {
    const labels = this.revenueTimeline.map((p) => p.label || p.date);
    const values = this.revenueTimeline.map((p) => p.value);

    this.revenueChartData = {
      labels,
      datasets: [
        {
          label: this.translate.instant(
            'STATISTICS.DASHBOARD.CHARTS.REVENUE_DATASET',
          ),
          data: values,
          fill: true,
          borderColor: brandColor(),
          backgroundColor: brandColor(500, 0.1),
          tension: 0.4,
          pointRadius: 2,
          pointHoverRadius: 6,
          pointBackgroundColor: brandColor(),
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
        },
      ],
    };

    this.revenueChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: '#1e293b',
          titleColor: '#fff',
          bodyColor: '#fff',
          padding: 12,
          cornerRadius: 8,
          callbacks: {
            label: (context) => {
              const value = context.parsed.y;
              return new Intl.NumberFormat(this.getLocale(), {
                style: 'currency',
                currency: 'EUR',
              }).format(value);
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: '#64748b',
            font: { size: 11 },
            maxRotation: 0,
          },
        },
        y: {
          grid: {
            color: '#f1f5f9',
          },
          ticks: {
            color: '#64748b',
            font: { size: 11 },
            callback: (value) => {
              if (typeof value === 'number') {
                return value >= 1000
                  ? `${(value / 1000).toFixed(0)}k€`
                  : `${value}€`;
              }
              return value;
            },
          },
          beginAtZero: true,
        },
      },
      interaction: {
        intersect: false,
        mode: 'index',
      },
    };
  }

  private buildPaymentChart(): void {
    this.paymentChartData = {
      labels: this.paymentMethods.map((p) =>
        this.translate.instant(`PAYMENT_TYPES.${p.name}`),
      ),
      datasets: [
        {
          data: this.paymentMethods.map((p) => p.value),
          backgroundColor: this.paymentMethods.map((p) => p.color || brandColor()),
          borderWidth: 0,
          borderRadius: 4,
          barThickness: 24,
        },
      ],
    };

    this.paymentChartOptions = {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: '#1e293b',
          titleColor: '#fff',
          bodyColor: '#fff',
          borderColor: '#334155',
          borderWidth: 1,
          padding: 12,
          cornerRadius: 8,
          callbacks: {
            label: (context) => {
              const value = context.parsed.x;
              const formatted = new Intl.NumberFormat(this.getLocale(), {
                style: 'currency',
                currency: 'EUR',
              }).format(value);
              return formatted;
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: '#64748b',
            font: { size: 11 },
            callback: (value) => {
              if (typeof value === 'number') {
                return value >= 1000
                  ? `${(value / 1000).toFixed(0)}k€`
                  : `${value}€`;
              }
              return value;
            },
          },
        },
        y: {
          grid: {
            display: false,
          },
          ticks: {
            color: '#334155',
            font: { size: 12, weight: 500 },
          },
        },
      },
    } as ChartOptions;
  }

  // =========================================================================
  // INSIGHTS
  // =========================================================================

  private generateInsights(): void {
    if (!this.kpis) return;

    this.insights = [];

    // Revenue insight
    if (this.kpis.revenue.trend && this.kpis.revenue.trend > 0.1) {
      this.insights.push({
        icon: 'pi-trending-up',
        type: 'success',
        message: this.translate.instant(
          'STATISTICS.DASHBOARD.INSIGHTS.REVENUE_UP',
          { percent: (this.kpis.revenue.trend * 100).toFixed(1) },
        ),
      });
    } else if (this.kpis.revenue.trend && this.kpis.revenue.trend < -0.1) {
      this.insights.push({
        icon: 'pi-trending-down',
        type: 'warning',
        message: this.translate.instant(
          'STATISTICS.DASHBOARD.INSIGHTS.REVENUE_DOWN',
          { percent: Math.abs(this.kpis.revenue.trend * 100).toFixed(1) },
        ),
      });
    }

    // New patients insight
    if (this.kpis.newPatients.trend && this.kpis.newPatients.trend < -0.1) {
      this.insights.push({
        icon: 'pi-users',
        type: 'warning',
        message: this.translate.instant(
          'STATISTICS.DASHBOARD.INSIGHTS.NEW_PATIENTS_DOWN',
        ),
      });
    }

    // Occupancy insight
    if (this.kpis.occupancyRate.value < 60) {
      this.insights.push({
        icon: 'pi-calendar',
        type: 'info',
        message: this.translate.instant(
          'STATISTICS.DASHBOARD.INSIGHTS.LOW_OCCUPANCY',
          { value: this.kpis.occupancyRate.value.toFixed(1) },
        ),
      });
    }

    // Default insight if none
    if (this.insights.length === 0) {
      this.insights.push({
        icon: 'pi-check-circle',
        type: 'success',
        message: this.translate.instant(
          'STATISTICS.DASHBOARD.INSIGHTS.STABLE_PERFORMANCE',
        ),
      });
    }
  }

  private getLocale(): string {
    const lang = (this.translate.currentLang || 'es').toLowerCase();
    if (lang.startsWith('en')) return 'en-US';
    if (lang.startsWith('ca')) return 'ca-ES';
    return 'es-ES';
  }

  // =========================================================================
  // NAVIGATION
  // =========================================================================

  onTreatmentClick(item: TopItem): void {
    // TODO: Navigate to treatment detail
    console.log('Treatment clicked:', item);
  }

  onPatientClick(item: TopItem): void {
    const routerLink = this.getPatientRouterLink(item.id);
    if (routerLink) {
      this.router.navigate([routerLink]);
    }
  }

  seeAllTreatments(): void {
    // TODO: Navigate to treatments section
    console.log('See all treatments');
  }

  seeAllPatients(): void {
    // TODO: Navigate to patients section
    console.log('See all patients');
  }

  private getPatientRouterLink(
    patientId: number | string | undefined,
  ): string | undefined {
    const customer = this.session.getCustomer();
    const customerIdentifier = customer?.identifier || customer?.id;
    if (!customerIdentifier || !patientId) return undefined;
    return `/${customerIdentifier}/patients/${patientId}/details`;
  }
}
